/**
 * 场景一：首页初始化 - 合并查询 API
 *
 * 将原本 16 个串行请求合并为 2 个并行请求组：
 * - AppConfigInit: 公共配置（无需登录态，可走 Storage 缓存）
 * - UserDataInit: 用户数据（需登录态，每次实时请求）
 */
import { fetcher } from '@/services/graphql/fetcher';
import { APP_CONFIG_INIT_QUERY, USER_DATA_INIT_QUERY } from '@/services/graphql/docs';
import { cachedQuery } from '@/utils/cachedQuery';
import { SettingKey } from '@/types';

// 缓存时间常量（毫秒）
const APP_CONFIG_TTL = 5 * 60 * 1000; // 5 分钟

/**
 * AppConfigInit 返回数据类型
 */
export interface AppConfigInitData {
  settings: AnyObject[];
  activeChannel: AnyObject;
  getWeappDynamicRouteConfig: {
    tabbarConfig: AnyObject[];
    eventTrackingConfig: AnyObject[];
  };
  maxDiscountMembershipPlan: AnyObject | null;
  getLevelConfig: AnyObject | null;
}

/**
 * UserDataInit 返回数据类型
 */
export interface UserDataInitData {
  activeCustomer: AnyObject | null;
  getUserMember: AnyObject | null;
  getVirtualCurrencyBalance: number | null;
  getOwnCustomlevel: AnyObject | null;
  getDistributor: AnyObject | null;
}

/**
 * 获取应用配置初始化数据（合并组 A）
 * 
 * 合并的接口：
 * - settings (精选商品配置)
 * - customActiveChannel (店铺配置)
 * - getWeappDynamicRouteConfig (tabbar和功能页面配置)
 * - maxDiscountMembershipPlan (最大优惠的会员卡)
 * - getLevelConfig (会员等级配置)
 * 
 * 使用 cachedQuery 缓存，TTL 5 分钟
 */
export const fetchAppConfigInit = async (): Promise<AppConfigInitData | null> => {
  // 与 app.ts getSettings 中使用的 keyNames 保持一致
  const keyNames = [
    SettingKey.memberCardProductGroup,
    SettingKey.personalCenterProductGroup,
    SettingKey.searchResultProductGroup,
    SettingKey.shoppingCartProductGroup,
    SettingKey.couponPageProductGroup,
    SettingKey.productDetailProductGroup,
    SettingKey.canBackProductDetailPage,
    SettingKey.forumRecommendProductGroup,
  ];

  return cachedQuery<AppConfigInitData | null>(
    'appConfig',
    APP_CONFIG_TTL,
    async () => {
      const res = await fetcher.query(APP_CONFIG_INIT_QUERY, {
        variables: { keyNames },
      });
      const data = res.data.value;
      if (data) {
        return {
          settings: data.settings ?? [],
          activeChannel: data.customActiveChannel?.customFields ?? {},
          getWeappDynamicRouteConfig: data.getWeappDynamicRouteConfig ?? {
            tabbarConfig: [],
            eventTrackingConfig: [],
          },
          maxDiscountMembershipPlan: data.maxDiscountMembershipPlan ?? null,
          getLevelConfig: data.getLevelConfig ?? null,
        };
      }
      return null;
    }
  );
};

/**
 * 获取用户数据初始化（合并组 B）
 * 
 * 合并的接口：
 * - activeCustomer (用户信息)
 * - getUserMember (会员信息)
 * - getVirtualCurrencyBalance (虚拟币余额)
 * - getOwnCustomlevel (会员等级)
 * - getDistributor (分销员数据)
 * 
 * 不使用缓存，每次实时请求
 */
export const fetchUserDataInit = async (): Promise<UserDataInitData | null> => {
  const res = await fetcher.query(USER_DATA_INIT_QUERY, {});
  const data = res.data.value;
  if (data) {
    return {
      activeCustomer: data.activeCustomer ?? null,
      getUserMember: data.getUserMember ?? null,
      getVirtualCurrencyBalance: data.getVirtualCurrencyBalance ?? null,
      getOwnCustomlevel: data.getOwnCustomlevel ?? null,
      getDistributor: data.getDistributor ?? null,
    };
  }
  return null;
};

